import styled from '@emotion/styled';
import { useSelector } from 'react-redux';
import { ConversieButtons } from 'shared/conversie';
import { Heading, LabelText, Currency, Percentage, ResultSummary } from 'shared/components';
import { comparisonFormatter } from '@dnhb/formatters';
import { whiteListUrlParams } from '../config';

import { getParamsFromState } from 'shared/helpers';
const Wrapper = styled.div`
  > div:not(:last-of-type) {
    margin-bottom: 24px;
  }
`;

const OutputResults = () => {
  const {
    settings,
    app,
    input,
    output: {
      already_in_lowest_risk_class,
      can_save,
      current_risk_class,
      original_risk_class,
      next_risk_class,
      monthly_saving,
      repayment_for_next_risk_class,
      next_interest_rate_reduction,
      interest_rate_reduction,
    },
  } = useSelector(state => state);
  const ResultHeader = () => {
    if (already_in_lowest_risk_class) {
      return (
        <>
          <Heading>
            <strong>Gefeliciteerd!</strong> Je hypotheek valt al in juiste risicoklasse
          </Heading>
          <LabelText>Mogelijk kun je nog wel op een andere manier besparen. Vraag je adviseur.</LabelText>
        </>
      );
    }
    if (can_save) {
      return (
        <>
          <Heading>
            Je kunt mogelijk <Currency value={monthly_saving} fontWeight="800" disableFontSizeLimitOfPrefix /> per maand
            besparen
          </Heading>
          <LabelText>
            Dit is een rentedaling van{' '}
            <Percentage value={interest_rate_reduction} fontWeight="800" disableFontSizeLimitOfPrefix />
          </LabelText>
        </>
      );
    }
    return (
      <>
        <Heading>
          <strong>Helaas.</strong> Je kunt nu nog niet profiteren van een lagere rente
        </Heading>
        <LabelText>
          Als je <Currency value={repayment_for_next_risk_class} fontWeight="800" disableFontSizeLimitOfPrefix />{' '}
          aflost, valt je hypotheek in een lagere risicoklasse. Je hebt dan recht op een rentedaling van{' '}
          <Percentage value={next_interest_rate_reduction} fontWeight="800" disableFontSizeLimitOfPrefix />.
        </LabelText>
      </>
    );
  };

  return (
    <Wrapper>
      <ResultHeader />
      {!already_in_lowest_risk_class && (
        <>
          <ResultSummary
            title={`Oorspronkelijke risicoklasse ${original_risk_class.nhg ? 'NHG' : ''}`}
            score={
              <>
                {comparisonFormatter(original_risk_class.comparison)}{' '}
                <Percentage value={original_risk_class.percentage} fontWeight="800" />
              </>
            }
          >
            De risicoklasse is de verhouding tussen de schuldrest van je hypotheek en de waarde van de woning. Leende je
            net zoveel geld (of meer) dan je woning toen waard was, dan is de Oorspronkelijke risicoklasse 100%
            &apos;marktwaarde&apos; (of meer). De bank loopt dan een groter risico wanneer je in betalingsproblemen
            komt. Je betaalt daarvoor een opslag-% op de zogenaamde basisrente. Heb je een Nationale Hypotheek Garantie
            (NHG) dan is dat risico afgedekt.
          </ResultSummary>
          {can_save ? (
            <ResultSummary
              title={`Huidige risicoklasse ${current_risk_class.nhg ? 'NHG' : ''}`}
              score={
                <>
                  {comparisonFormatter(current_risk_class.comparison)}{' '}
                  <Percentage value={current_risk_class.percentage} fontWeight="800" />
                </>
              }
            >
              Voor je huidige risicoklasse vergelijken we wat je nu aan schuldrest hebt met wat je woning waard is.
              Daarbij kijken we of jouw geldverstrekker wel of niet (automatisch) de rente aanpast, als je in een lagere
              risicoklasse valt. Zo niet, dan kun je vermoedelijk besparen! Let op: mogelijk hanteerde de bank bij het
              afsluiten van je hypotheek andere risicoklasses dan waarvan wij nu uit gaan; de mogelijke besparing wijkt
              dan af. Je adviseur kan dat voor je nagaan.
            </ResultSummary>
          ) : (
            <ResultSummary
              title={`Volgende risicoklasse ${next_risk_class.nhg ? 'NHG' : ''}`}
              score={
                <>
                  {comparisonFormatter(next_risk_class.comparison)}{' '}
                  <Percentage value={next_risk_class.percentage} fontWeight="800" />
                </>
              }
            >
              We gebruiken de risicoklasses die je geldverstrekker nu hanteert en bepalen hoeveel je nog moet aflossen
              vóór je in een volgende (voordeliger) klasse valt. We doen dit op basis van wat je invulde bij huidige
              restschuld en woningwaarde. Let op! Het kan zijn, dat je bank bij het afsluiten van de hypotheek andere
              risicoklasses hanteerde; onze berekening kan dan afwijken van de werkelijkheid. Je adviseur kan je nader
              informeren.
            </ResultSummary>
          )}
        </>
      )}
      <ConversieButtons
        app={app}
        settings={settings}
        urlLeadFormParams={getParamsFromState({ input }, whiteListUrlParams)}
      />
    </Wrapper>
  );
};

export default OutputResults;
